import { View } from "ui/atoms/view";
import { Text } from "react-native";
import { Control, Controller } from "react-hook-form";
import { CreateCalendarEventForm } from "app/services/api/types/calendar/requests/CreateCalendarEventForm";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { FormControlledDatePicker } from "ui/molecules/form/FormControlledDatePicker";
import { numberValidation, requiredValidation } from "ui/utils/Validators";
import { CheckboxGroup } from "ui/gluestack/checkbox";
import { SelectableOptionWithControl } from "../molecules/SelectableOptionWithControl";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from "ui/gluestack/form-control";
import { AlertCircleIcon } from "ui/gluestack/icon";

interface Props {
  control: Control<CreateCalendarEventForm>;
}

export function EventEndDetails({ control }: Props) {
  return (
    <View className="mt-5">
      <Text className="text-gray-500 mb-4">Ends</Text>
      <View className=" border border-secondary-50 rounded-xl p-6">
        <Controller
          control={control}
          name="endsOption"
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <FormControl isInvalid={!!error}>
                <CheckboxGroup
                  value={value ? [value] : []}
                  onChange={(val) => {
                    onChange(val.length ? val[val.length - 1] : undefined);
                  }}
                  className="gap-4"
                >
                  <SelectableOptionWithControl
                    value="never"
                    label="Never"
                    className="ml-3"
                  />
                  <SelectableOptionWithControl
                    value="date"
                    label="On"
                    className="ml-3"
                    controlElement={
                      <FormControlledDatePicker
                        id="recurrence_rule.end_date"
                        name="recurrence_rule.end_date"
                        control={control}
                        placeholder="00/00/0000"
                        isDisabled={value !== "date"}
                        rules={{
                          validate: {
                            requiredIf: (fieldValue) => {
                              if (value === "date" && !fieldValue) {
                                return "End date is required";
                              }
                              return true;
                            },
                          },
                        }}
                      />
                    }
                  />

                  <SelectableOptionWithControl
                    value="occurrences"
                    controlElement={
                      <View className="flex-row items-center gap-2">
                        <FormControlledInput
                          id="recurrence_rule.max_occurrences"
                          name="recurrence_rule.max_occurrences"
                          control={control}
                          placeholder="0"
                          className="flex-row items-center"
                          isDisabled={value !== "occurrences"}
                          textInputClassName="flex-1 max-w-10"
                          prefix={
                            <Text className=" text-gray-600 mr-2 text-sm">
                              After
                            </Text>
                          }
                          postfix={
                            <Text className="text-sm text-gray-600 ml-2">
                              times
                            </Text>
                          }
                          rules={{
                            validate: {
                              numberValidation,
                              requiredIf: (fieldValue) => {
                                if (value === "occurrences" && !fieldValue) {
                                  return "Max occurrences is required";
                                }
                                return true;
                              },
                            },
                          }}
                        />
                      </View>
                    }
                  />

                  <FormControlError>
                    <FormControlErrorIcon
                      as={AlertCircleIcon}
                      className="text-error-500"
                      size="sm"
                    />
                    <FormControlErrorText className="text-error-500 text-sm">
                      {error?.message}
                    </FormControlErrorText>
                  </FormControlError>
                </CheckboxGroup>
              </FormControl>
            );
          }}
          rules={{
            required: requiredValidation("Ends"),
          }}
        />
      </View>
    </View>
  );
}
